import type {
  DefaultMantineColor,
  ModalProps} from '@mantine/core';
import {
  Box,
  Group,
  Modal,
  Text,
} from '@mantine/core';
import type {ReactNode} from 'react';
import {Button} from 'src/design/atoms/Button';

/**
 * Confirm/cancel modal dialog — composes the `design/atoms/Button` atom.
 * @registry Yes/No confirmation modal with configurable title/body/colors.
 *
 * Ported from `library-m/overlays/ConfirmationModal`. Prop surface kept
 * identical (same defaults for `title`, `body`, `confirmationButtonColor`,
 * `buttonTextYes`/`buttonTextNo`) so migrating call sites is mechanical. The
 * "No"/cancel action uses a `subtle` button (via `Button`'s `variant` prop)
 * rather than porting the separate `DialogButton` wrapper — one atom, two
 * variants, instead of two near-identical button components.
 */
export interface ConfirmDialogProps
  extends Omit<ModalProps, 'onClose' | 'title' | 'children'> {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  body?: ReactNode | string;
  confirmationButtonColor?: DefaultMantineColor;
  buttonTextYes?: string;
  buttonTextNo?: string;
  confirmationButtonDisabled?: boolean;
}

export const ConfirmDialog = ({
  opened,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  body = 'Are you sure you want to take this action?',
  confirmationButtonColor = 'yellow',
  buttonTextYes = 'Yes',
  buttonTextNo = 'No',
  confirmationButtonDisabled = false,
  ...modalProps
}: ConfirmDialogProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      styles={{
        title: {
          fontWeight: 700,
        },
      }}
      centered
      {...modalProps}>
      {typeof body === 'string' ? (
        <Text size="sm" c="grey" mb="lg">
          {body}
        </Text>
      ) : (
        <Box mb="lg">{body}</Box>
      )}
      <Group justify="end">
        <Button variant="subtle" color="black" onClick={onClose}>
          {buttonTextNo}
        </Button>
        <Button
          variant="filled"
          color={confirmationButtonColor}
          onClick={onConfirm}
          disabled={confirmationButtonDisabled}>
          {buttonTextYes}
        </Button>
      </Group>
    </Modal>
  );
};
